import { useCallback, useEffect, useMemo, useState } from "react";
import { endOfMonth, formatISO, startOfMonth, subMonths } from "date-fns";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { pluralizeWithCount } from "@common/helpers/string";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { AR_AGING_REPORT_PACKAGE_REPORT_TYPES } from "@common/types/reports/report-package";
import { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import {
    useAccrualEntityIdsWithLoading,
    useLedgersByEntityIds,
} from "@api/ledger.api";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import {
    BASE_REPORT_PACKAGE_REPORT_TYPES,
    REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP,
    canSelectAccrualBasis,
    resolveReportPackageLedgerId,
} from "./reportPackageBasis.lib";

const BASIS_OPTIONS = [
    AccountingLedgerBasis.cash,
    AccountingLedgerBasis.accruals,
];

function buildLastMonthRange(): DateOnlyDateRange {
    const lastMonth = subMonths(new Date(), 1);
    return {
        start: formatISO(startOfMonth(lastMonth), { representation: "date" }),
        end: formatISO(endOfMonth(lastMonth), { representation: "date" }),
    };
}

export function useReportPackageSettings({
    show,
    defaultEntityId,
    defaultDateRange,
    ledgerId,
}: {
    show: boolean;
    defaultEntityId?: number;
    defaultDateRange?: DateOnlyDateRange;
    ledgerId?: string;
}) {
    const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>(
        defaultEntityId ? [defaultEntityId] : [],
    );
    const [basis, setBasis] = useState<AccountingLedgerBasis>(
        AccountingLedgerBasis.cash,
    );
    const [dateRange, setDateRange] = useState<DateOnlyDateRange>(
        () => defaultDateRange ?? buildLastMonthRange(),
    );
    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_REPORT_PACKAGE_MODAL_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    useEffect(() => {
        if (show) {
            setSelectedEntityIds(defaultEntityId ? [defaultEntityId] : []);
        }
    }, [show, defaultEntityId]);

    useEffect(() => {
        if (show && defaultDateRange) {
            setDateRange(defaultDateRange);
        }
    }, [show, defaultDateRange]);

    const availableEntities = useEntitiesWithAccountingAvailable();
    const availableEntityIds = useMemo(
        () => availableEntities.map((entity) => entity.id),
        [availableEntities],
    );
    const { accrualEntityIds, isLoading: isAccrualLookupLoading } =
        useAccrualEntityIdsWithLoading(availableEntityIds);
    const { data: ledgersByEntityId } =
        useLedgersByEntityIds(availableEntityIds);
    const isAccrualAvailable = canSelectAccrualBasis({
        selectedEntityIds,
        accrualEntityIds,
        isLoading: isAccrualLookupLoading,
    });
    const effectiveBasis =
        basis === AccountingLedgerBasis.accruals && isAccrualAvailable
            ? AccountingLedgerBasis.accruals
            : AccountingLedgerBasis.cash;

    const selectedEntities = useMemo(
        () => availableEntities.filter((e) => selectedEntityIds.includes(e.id)),
        [selectedEntityIds, availableEntities],
    );

    const entityTriggerValue = useMemo(() => {
        if (selectedEntities.length === 0) {
            return undefined;
        }
        if (selectedEntities.length === 1) {
            return <EntityDisplay entity={selectedEntities[0]} />;
        }
        return (
            <EntityAvatarGroup
                entities={selectedEntities}
                max={3}
                size="xs"
                label={pluralizeWithCount("Entity", selectedEntities.length)}
            />
        );
    }, [selectedEntities]);

    const entityItems = useMemo<Array<PickerItemData<number>>>(
        () =>
            availableEntities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [availableEntities],
    );

    const basisItems = useMemo<Array<PickerItemData<AccountingLedgerBasis>>>(
        () =>
            BASIS_OPTIONS.map((option) => {
                const isAccrualDisabled =
                    option === AccountingLedgerBasis.accruals &&
                    !isAccrualAvailable;
                return {
                    value: option,
                    label: getAccountingBasisLabel(option),
                    disabled: isAccrualDisabled,
                    disabledReason: isAccrualDisabled
                        ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
                        : undefined,
                };
            }),
        [isAccrualAvailable],
    );

    const availableReportTypes = useMemo(
        () =>
            effectiveBasis === AccountingLedgerBasis.accruals
                ? [
                      ...BASE_REPORT_PACKAGE_REPORT_TYPES,
                      ...AR_AGING_REPORT_PACKAGE_REPORT_TYPES,
                  ]
                : BASE_REPORT_PACKAGE_REPORT_TYPES,
        [effectiveBasis],
    );

    const handleBasisChange = useCallback(
        (nextBasis: AccountingLedgerBasis) => {
            if (
                nextBasis === AccountingLedgerBasis.accruals &&
                !isAccrualAvailable
            ) {
                return;
            }
            setBasis(nextBasis);
        },
        [isAccrualAvailable],
    );

    const resetBasis = useCallback(() => {
        setBasis(AccountingLedgerBasis.cash);
    }, []);

    const resolvedLedgerId = useMemo(
        () =>
            resolveReportPackageLedgerId({
                ledgersByEntityId,
                selectedEntityIds,
                basis: effectiveBasis,
                fallbackLedgerId: ledgerId,
            }),
        [ledgersByEntityId, selectedEntityIds, effectiveBasis, ledgerId],
    );

    return {
        selectedEntityIds,
        setSelectedEntityIds,
        effectiveBasis,
        handleBasisChange,
        resetBasis,
        dateRange,
        setDateRange,
        pinnedPresets,
        onTogglePin,
        entityTriggerValue,
        availableEntityIds,
        entityItems,
        basisItems,
        availableReportTypes,
        resolvedLedgerId,
        isAccrualAvailable,
        accrualEntityIds,
        isAccrualLookupLoading,
        ledgersByEntityId,
    };
}
